/**
 * Error Parsing Library - Error Codes
 *
 * Maps protocol-specific error codes to human-readable messages.
 * Includes AAVE, Euler, and common EVM errors.
 */

import { aaveContractErrors } from '@/config/aaveContractErrors'
import type { ErrorCategory } from './types'

/**
 * AAVE Protocol error codes (1-91).
 * Re-exported from the canonical source to avoid duplication.
 * See: https://docs.aave.com/developers/guides/troubleshooting-errors
 */
export const aaveErrors: Record<string, string> = aaveContractErrors

/**
 * Euler Protocol error selectors (4-byte function selectors for custom errors).
 * Canonical selectors sourced from packages/evc-sdk/src/utils/errors/*.json.
 */
export const eulerErrorSelectors: Record<string, { name: string; message: string }> = {
  // ── EVK vault errors (evk-errors.json) ──
  '030fd1cc': { name: 'E_OnlyAssetCanDeposit', message: 'Only the underlying asset can be deposited.' },
  '08e2ce17': { name: 'E_Unauthorized', message: 'You are not authorized to perform this action.' },
  '0a1d0efb': {
    name: 'E_LiquidationCoolOff',
    message: 'Liquidation cool-off period is active. Please try again later.',
  },
  '0a4e9d0b': { name: 'E_ConfigAmountTooLargeToEncode', message: 'Configuration amount is too large to process.' },
  '0dc316c4': { name: 'E_MinYield', message: 'Minimum yield requirement not met.' },
  '0e7db831': { name: 'E_ViolatorLiquidityDeferred', message: 'Violator liquidity check was deferred.' },
  '13790bf0': {
    name: 'E_ControllerDisabled',
    message: 'Controller is disabled for this vault. Please enable it first.',
  },
  '2082e200': { name: 'E_EmptyError', message: 'An empty error was returned by the vault.' },
  '2c82283f': { name: 'E_FeeExceedsBPS', message: 'Fee exceeds the maximum allowed basis points.' },
  '304fad62': { name: 'E_InsufficientAssets', message: 'Insufficient assets in the vault for this operation.' },
  '34373fbc': {
    name: 'E_AccountLiquidity',
    message: 'Account liquidity check failed. Your position may become undercollateralized.',
  },
  '35a43994': { name: 'E_IRMUpdateUnauthorized', message: 'Interest rate model update is not authorized.' },
  '370f9b38': { name: 'E_BadAddress', message: 'Invalid address provided.' },
  '3ac94a30': { name: 'E_ProxyMetadata', message: 'Invalid proxy metadata.' },
  '3b77f101': { name: 'E_ExcessiveRepayAmount', message: 'Repayment amount exceeds the outstanding debt.' },
  '426073f2': { name: 'E_SupplyCapExceeded', message: 'Supply cap has been reached. No more deposits are accepted.' },
  '43855d0f': { name: 'E_NoLiability', message: 'No outstanding liability found for this account.' },
  '458cbc27': { name: 'E_LTVBorrow', message: 'Loan-to-value ratio exceeded for borrowing.' },
  '459f5f6e': { name: 'E_ZeroAddress', message: 'Zero address is not allowed.' },
  '5b923371': { name: 'E_NoPriceOracle', message: 'No price oracle configured for this asset.' },
  '6081f51b': { name: 'E_ZeroAssets', message: 'Cannot operate with zero assets.' },
  '64e06479': { name: 'E_AmountTooLargeToEncode', message: 'The amount is too large to process.' },
  '6d588708': { name: 'E_NotController', message: 'Caller is not the controller for this account.' },
  '6ef90ef1': { name: 'E_BorrowCapExceeded', message: 'Borrow cap has been reached. No more borrows are accepted.' },
  '70ba0d81': { name: 'E_Initialized', message: 'Vault is already initialized.' },
  '73748093': { name: 'E_InsufficientBalance', message: 'Insufficient token balance.' },
  '74f36063': { name: 'E_Reentrancy', message: 'Reentrancy detected. Please try again.' },
  '750f8817': { name: 'E_OperationDisabled', message: 'This operation is currently disabled on the vault.' },
  '89563211': { name: 'E_InvalidLTVAsset', message: 'Invalid LTV configuration for this asset.' },
  '9388c334': { name: 'E_DebtAmountTooLargeToEncode', message: 'Debt amount is too large to process.' },
  '94d8be99': { name: 'E_SelfLiquidation', message: 'Cannot liquidate your own position.' },
  '9773bb71': {
    name: 'E_TransferFromFailed',
    message: 'Token transfer failed. You may have insufficient balance or allowance.',
  },
  '98d6fe33': { name: 'E_FlashLoanNotRepaid', message: 'Flash loan was not repaid.' },
  '9af486d3': { name: 'E_BadBorrowCap', message: 'Invalid borrow cap configuration.' },
  '9b314d55': { name: 'E_BadCollateral', message: 'Invalid collateral configuration.' },
  '9b441636': { name: 'E_BadSharesOwner', message: 'Invalid shares owner.' },
  '9b559a75': { name: 'E_CollateralDisabled', message: 'This collateral asset is disabled.' },
  '9f428ac4': {
    name: 'E_InsufficientAllowance',
    message: 'Insufficient token allowance. Please approve the token first.',
  },
  a8af73b4: { name: 'E_BadSharesReceiver', message: 'Invalid shares receiver address.' },
  aa19a73d: { name: 'E_BadMaxLiquidationDiscount', message: 'Invalid max liquidation discount.' },
  ae321632: { name: 'E_TransientState', message: 'Operation cannot be performed during a batch transaction.' },
  aeb1b8c6: { name: 'E_BadSupplyCap', message: 'Invalid supply cap configuration.' },
  b2be531b: { name: 'E_RepayTooMuch', message: 'Repayment amount exceeds the outstanding debt.' },
  b53a01cc: { name: 'E_OutstandingDebt', message: 'You have outstanding debt that must be repaid first.' },
  be4dab5d: { name: 'E_DeploymentFailed', message: 'Contract deployment failed.' },
  c9e70637: { name: 'E_BadAssetReceiver', message: 'Invalid asset receiver address.' },
  ca0985cf: { name: 'E_ZeroShares', message: 'Cannot operate with zero shares.' },
  cf4d8f28: { name: 'E_BadFee', message: 'Invalid fee configuration.' },
  d0ccf7a0: { name: 'E_NotHookTarget', message: 'Caller is not the hook target.' },
  d36283e7: { name: 'E_LTVLiquidation', message: 'Loan-to-value ratio makes this position liquidatable.' },
  d59cc978: { name: 'E_ZeroConversionPrice', message: 'Conversion price is zero.' },
  dac9cb7d: { name: 'E_InsufficientDebt', message: 'Insufficient debt to perform this operation.' },
  f077d877: { name: 'E_InsufficientCash', message: 'Insufficient cash in the vault.' },
  f0991feb: { name: 'E_CheckUnauthorized', message: 'Status check is not authorized.' },
  f2fbfb2d: { name: 'E_SelfTransfer', message: 'Cannot transfer to yourself.' },
  fea48513: { name: 'E_NotSupported', message: 'This operation is not supported.' },

  // ── EVC errors (evc-errors.json) ──
  '0ddfd8da': { name: 'EVC_ControlCollateralReentrancy', message: 'Control collateral reentrancy detected.' },
  '38ae747c': { name: 'EVC_EmptyError', message: 'An empty error was returned by the EVC.' },
  '44263592': { name: 'EVC_PermitDisabledMode', message: 'EVC permits are disabled.' },
  '4cd2d4f3': { name: 'EVC_BatchPanic', message: 'A panic occurred during batch execution.' },
  '5217b8ae': {
    name: 'EVC_OnBehalfOfAccountNotAuthenticated',
    message: 'Account not authenticated for this operation.',
  },
  '655156bd': { name: 'EVC_InvalidOperatorStatus', message: 'Invalid operator status.' },
  '7c1b2908': { name: 'EVC_ChecksReentrancy', message: 'Checks reentrancy detected.' },
  '7c9bb1cb': { name: 'EVC_InvalidTimestamp', message: 'Invalid timestamp provided.' },
  '8133abd1': { name: 'EVC_InvalidAddress', message: 'Invalid address provided to the EVC.' },
  a82b84bb: { name: 'EVC_InvalidNonce', message: 'Invalid nonce. The transaction may have already been executed.' },
  b83566c5: { name: 'EVC_SimulationBatchNested', message: 'Simulation batch cannot be nested.' },
  bb6de1c7: { name: 'EVC_InvalidValue', message: 'Invalid value provided to the EVC.' },
  d4b0e4d3: { name: 'EVC_RevertedBatchResult', message: 'Batch execution reverted.' },
  d80a9cac: { name: 'EVC_LockdownMode', message: 'Account is in lockdown mode.' },
  e07f2e6b: { name: 'EVC_NotAuthorized', message: 'Not authorized to perform this EVC operation.' },
  e85c620e: { name: 'EVC_InvalidData', message: 'Invalid data provided to the EVC.' },
  f1be4519: { name: 'EVC_ControllerViolation', message: 'Controller violation detected.' },

  // ── Periphery errors (evk-periphery-errors.json) ──
  '0977240b': { name: 'SwapVerifier_pastDeadline', message: 'Swap deadline has passed. Please try again.' },
  '1c27d2c0': { name: 'SwapVerifier_debtMax', message: 'Swap would result in excessive debt.' },
  '577a3929': { name: 'HTG_VaultPaused', message: 'This vault is currently paused.' },
  '70b55f7e': { name: 'Swapper_UnknownMode', message: 'Unknown swap mode.' },
  '807d9b7b': { name: 'UniswapV2Handler_InvalidPath', message: 'Invalid swap path for Uniswap V2.' },
  '8f22cca2': { name: 'UniswapV3Handler_InvalidPath', message: 'Invalid swap path for Uniswap V3.' },
  b2451501: { name: 'Swapper_TargetDebt', message: 'Swap target debt check failed.' },
  ce172551: { name: 'Swapper_Reentrancy', message: 'Swapper reentrancy detected.' },
  d6bd9351: { name: 'SwapVerifier_skimMin', message: 'Swap output below minimum (slippage too high).' },
  da629180: { name: 'Swapper_UnsupportedMode', message: 'Unsupported swap mode.' },
  e2f8a31a: { name: 'IRMFactory_ExcessiveInterestRate', message: 'Interest rate is excessively high.' },

  // ── Price oracle errors (price-oracle-errors.json) ──
  '06930594': { name: 'PriceOracle_InvalidConfiguration', message: 'Price oracle is misconfigured for this asset.' },
  '4ca22af0': { name: 'PriceOracle_NotSupported', message: 'Price oracle does not support this asset.' },
  '52a1f2a0': { name: 'PriceOracle_Overflow', message: 'Price oracle overflow.' },
  a6e68d63: { name: 'PriceOracle_TooStale', message: 'Price oracle data is stale. Please try again later.' },
  d743df6a: { name: 'PriceOracle_InvalidAnswer', message: 'Price oracle returned an invalid answer.' },

  // ── Pyth contract native errors ──
  '025dbdd4': { name: 'Pyth_InsufficientFee', message: 'Insufficient fee for Pyth price update.' },
  de2c57fa: { name: 'Pyth_NoFreshUpdate', message: 'No fresh Pyth price update available.' },
  '14aebe68': { name: 'Pyth_PriceFeedNotFound', message: 'Pyth price feed not found for this asset.' },
  '45805f5d': { name: 'Pyth_PriceFeedNotFoundWithinRange', message: 'Pyth price feed not found within the requested time range.' },
  e60dce71: { name: 'Pyth_InvalidUpdateDataSource', message: 'Invalid Pyth update data source.' },
  e69ffece: { name: 'Pyth_InvalidUpdateData', message: 'Invalid Pyth price update data.' },
  '19abf40e': { name: 'Pyth_StalePrice', message: 'Pyth price data is stale. Please try again.' },

  // ── Euler PythOracle adapter errors ──
  '582013b8': { name: 'PythOracle_PriceZero', message: 'Pyth oracle returned a zero price.' },
  '0a1119bf': { name: 'PythOracle_InvalidAnswer', message: 'Pyth oracle returned an invalid answer.' },
  '90a94999': { name: 'PythOracle_Overflow', message: 'Pyth oracle price overflow.' },
  '8468b61d': { name: 'PythOracle_StalePythPrice', message: 'Pyth oracle price is stale. Please try again later.' },
  '0365e594': { name: 'PythOracle_ConfidenceTooWide', message: 'Pyth oracle confidence interval is too wide for a reliable price.' },
  a50f7073: { name: 'PythOracle_InvalidConfiguration', message: 'Pyth oracle adapter is misconfigured.' },
  '7499f3d1': { name: 'PythOracle_ConfidenceIntervalTooWide', message: 'Pyth oracle confidence interval exceeds the allowed maximum.' },

  // ── Common ERC20/OpenZeppelin errors ──
  '4b800e46': { name: 'ERC2612InvalidSigner', message: 'Invalid permit signer.' },
  '5274afe7': { name: 'SafeERC20FailedOperation', message: 'Token operation failed (SafeERC20).' },
  '62791302': { name: 'ERC2612ExpiredSignature', message: 'Permit signature has expired.' },
  '94280d62': { name: 'ERC20InvalidSpender', message: 'Invalid spender address.' },
  '96c6fd1e': { name: 'ERC20InvalidSender', message: 'Invalid sender address.' },
  e450d38c: { name: 'ERC20InsufficientBalance', message: 'Insufficient token balance.' },
  e602df05: { name: 'ERC20InvalidApprover', message: 'Invalid approver address.' },
  ec442f05: { name: 'ERC20InvalidReceiver', message: 'Invalid receiver address.' },
  fb8f41b2: { name: 'ERC20InsufficientAllowance', message: 'Insufficient token allowance.' },
  b39e1e6e: { name: 'ControllerDisabled', message: 'Controller is disabled.' },
  cf479181: { name: 'InsufficientBalance', message: 'Insufficient balance.' },
  ea8e4eb5: { name: 'NotAuthorized', message: 'Not authorized.' },
  d6bda275: { name: 'FailedCall', message: 'External call failed.' },
}

/**
 * Common EVM revert reasons and their user-friendly messages.
 */
export const evmRevertReasons: Record<string, string> = {
  // Transfer errors (specific patterns first — generic ones match via includes() and would shadow these)
  'ERC20: transfer amount exceeds balance': 'Insufficient token balance for this transfer.',
  'ERC20: transfer amount exceeds allowance': 'Please approve the token spending first.',
  'ERC20: insufficient allowance': 'Please approve the token spending first.',
  'ERC20: burn amount exceeds balance': 'Cannot burn more tokens than you have.',
  'transfer amount exceeds balance': 'You do not have enough tokens for this transfer.',
  'transfer amount exceeds allowance': 'Token spending allowance is insufficient. Please approve first.',

  // Approval errors
  'ERC20: approve to the zero address': 'Cannot approve to the zero address.',
  'ERC20: approve from the zero address': 'Invalid approval source.',

  // Ownership/Access errors
  'Ownable: caller is not the owner': 'Only the contract owner can perform this action.',
  'caller is not the owner': 'Only the contract owner can perform this action.',

  // Pausable
  'Pausable: paused': 'This contract is currently paused.',
  'Pausable: not paused': 'This contract is not paused.',

  // Reentrancy
  'ReentrancyGuard: reentrant call': 'Transaction would cause reentrancy.',

  // Math errors
  'SafeMath: addition overflow': 'Arithmetic overflow in calculation.',
  'SafeMath: subtraction overflow': 'Arithmetic underflow in calculation.',
  'SafeMath: multiplication overflow': 'Arithmetic overflow in calculation.',
  'SafeMath: division by zero': 'Cannot divide by zero.',

  // General
  'execution reverted': 'Transaction would fail. Please check your parameters.',
}

/**
 * Network-level error patterns.
 */
export const networkErrorPatterns: Array<{
  pattern: RegExp
  message: string
  category: ErrorCategory
  isRecoverable: boolean
  suggestedAction: import('./types').SuggestedAction
}> = [
  {
    pattern: /nonce too low/i,
    message: 'Transaction nonce is out of sync. Please try again.',
    category: 'network',
    isRecoverable: true,
    suggestedAction: 'retry',
  },
  {
    pattern: /nonce too high/i,
    message: 'Transaction nonce is out of sync. Please wait for pending transactions.',
    category: 'network',
    isRecoverable: true,
    suggestedAction: 'retry-later',
  },
  {
    pattern: /replacement transaction underpriced/i,
    message: 'Gas price too low to replace pending transaction. Try increasing gas.',
    category: 'network',
    isRecoverable: true,
    suggestedAction: 'retry',
  },
  {
    pattern: /transaction underpriced/i,
    message: 'Gas price too low. Please increase gas settings.',
    category: 'network',
    isRecoverable: true,
    suggestedAction: 'retry',
  },
  {
    pattern: /insufficient funds for gas/i,
    message: 'Not enough HYPE to pay for gas fees.',
    category: 'insufficient-funds',
    isRecoverable: false,
    suggestedAction: 'add-funds',
  },
  {
    pattern: /insufficient funds for (?:intrinsic transaction|transfer)/i,
    message: 'Insufficient funds in your wallet.',
    category: 'insufficient-funds',
    isRecoverable: false,
    suggestedAction: 'add-funds',
  },
  {
    pattern: /gas required exceeds allowance/i,
    message: 'Transaction requires more gas than allowed. Try reducing the amount.',
    category: 'network',
    isRecoverable: true,
    suggestedAction: 'adjust-amount',
  },
  {
    pattern: /intrinsic gas too low/i,
    message: 'Gas limit is too low for this transaction.',
    category: 'network',
    isRecoverable: true,
    suggestedAction: 'retry',
  },
  {
    pattern: /max fee per gas less than block base fee/i,
    message: 'Gas price is below the current network minimum.',
    category: 'network',
    isRecoverable: true,
    suggestedAction: 'retry-later',
  },
  {
    pattern: /request timed? ?out|TimeoutError|ETIMEDOUT|ESOCKETTIMEDOUT/i,
    message: 'Network request timed out. Please try again.',
    category: 'timeout',
    isRecoverable: true,
    suggestedAction: 'retry',
  },
  {
    pattern: /network error|failed to fetch|fetch failed/i,
    message: 'Network connection error. Please check your internet connection.',
    category: 'network',
    isRecoverable: true,
    suggestedAction: 'check-network',
  },
  {
    pattern: /rate limit|too many requests/i,
    message: 'Too many requests. Please wait a moment and try again.',
    category: 'rate-limit',
    isRecoverable: true,
    suggestedAction: 'retry-later',
  },
]

/**
 * User rejection patterns - these are not really errors, just user cancelled.
 */
export const userRejectionPatterns: RegExp[] = [
  /user rejected/i,
  /user denied/i,
  /user disapproved/i,
  /user cancelled/i,
  /rejected the request/i,
  /user refused/i,
  /action_rejected/i,
  /\buser cancel/i,
]

/**
 * Slippage and liquidity error patterns.
 */
export const slippageLiquidityPatterns: Array<{
  pattern: RegExp
  category: 'slippage' | 'liquidity'
  message: string
}> = [
  {
    pattern: /slippage/i,
    category: 'slippage',
    message: 'Price moved beyond your slippage tolerance. Try increasing slippage or reducing amount.',
  },
  {
    pattern: /INSUFFICIENT_OUTPUT_AMOUNT/i,
    category: 'slippage',
    message: 'Output amount would be less than minimum. Try increasing slippage tolerance.',
  },
  {
    pattern: /EXCESSIVE_INPUT_AMOUNT/i,
    category: 'slippage',
    message: 'Input amount would exceed maximum. Try increasing slippage tolerance.',
  },
  {
    pattern: /\bK$/m,
    category: 'liquidity',
    message: 'Insufficient liquidity in the pool for this trade size.',
  },
  {
    pattern: /INSUFFICIENT_LIQUIDITY/i,
    category: 'liquidity',
    message: 'Insufficient liquidity for this trade. Try a smaller amount.',
  },
  {
    pattern: /no swap quote available|quote not found/i,
    category: 'liquidity',
    message: 'No swap route available. Try a different token pair or smaller amount.',
  },
]
